/**
 * CodingQueue page — displays Pending Review invoices with AI-pre-populated
 * GL codes for the contract admin to verify, correct, or override.
 *
 * Requirements: 44.12, 44.13, 44.14
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invoiceService } from '@/services/invoiceService';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import type { InvoiceLineItem } from '@/types';

interface LineItemEdit {
  lineItemId: number;
  glCode: string;
  costCenter: string;
  budgetCategory: string;
  siteNumber: string;
}

export function CodingQueue() {
  const queryClient = useQueryClient();
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<number | null>(null);
  const [lineItemEdits, setLineItemEdits] = useState<Record<number, LineItemEdit>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data: invoices, isLoading, error, refetch } = useQuery({
    queryKey: ['invoices', 'coding-queue'],
    queryFn: () => invoiceService.getCodingQueue(),
  });

  const { data: invoiceDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['invoice', expandedInvoiceId],
    queryFn: () => invoiceService.getById(expandedInvoiceId!),
    enabled: expandedInvoiceId !== null,
  });

  const codeMutation = useMutation({
    mutationFn: ({ invoiceId, lineItems }: { invoiceId: number; lineItems: LineItemEdit[] }) =>
      invoiceService.codeInvoice(invoiceId, {
        lineItems: lineItems.map((li) => ({
          lineItemId: li.lineItemId,
          glCode: li.glCode || null,
          costCenter: li.costCenter || null,
          budgetCategory: li.budgetCategory || null,
          siteNumber: li.siteNumber || null,
        })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices', 'coding-queue'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', expandedInvoiceId] });
    },
  });

  const submitMutation = useMutation({
    mutationFn: (invoiceId: number) => invoiceService.submitForReview(invoiceId),
    onSuccess: () => {
      setSuccessMessage('Invoice submitted for VP review.');
      setExpandedInvoiceId(null);
      setLineItemEdits({});
      queryClient.invalidateQueries({ queryKey: ['invoices', 'coding-queue'] });
      setTimeout(() => setSuccessMessage(null), 3000);
    },
  });

  function initializeEdits(lineItems: InvoiceLineItem[]) {
    const edits: Record<number, LineItemEdit> = {};
    for (const li of lineItems) {
      edits[li.lineItemId] = {
        lineItemId: li.lineItemId,
        glCode: li.glCode ?? li.aiSuggestedGLCode ?? '',
        costCenter: li.costCenter ?? '',
        budgetCategory: li.budgetCategory ?? '',
        siteNumber: li.siteNumber ?? '',
      };
    }
    setLineItemEdits(edits);
  }

  function handleExpand(invoiceId: number) {
    if (expandedInvoiceId === invoiceId) {
      setExpandedInvoiceId(null);
      setLineItemEdits({});
    } else {
      setExpandedInvoiceId(invoiceId);
    }
  }

  function handleFieldChange(lineItemId: number, field: keyof LineItemEdit, value: string) {
    setLineItemEdits((prev) => ({
      ...prev,
      [lineItemId]: { ...prev[lineItemId], [field]: value },
    }));
  }

  function handleSaveCoding(invoiceId: number) {
    const items = Object.values(lineItemEdits);
    if (items.length === 0) return;
    codeMutation.mutate({ invoiceId, lineItems: items });
  }

  function handleSubmitForReview(invoiceId: number) {
    submitMutation.mutate(invoiceId);
  }

  // Initialize edits when detail loads
  if (invoiceDetail && expandedInvoiceId !== null && Object.keys(lineItemEdits).length === 0) {
    initializeEdits(invoiceDetail.lineItems);
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading coding queue…" />;
  }

  if (error) {
    return (
      <ErrorMessage
        message={(error as Error)?.message ?? 'Failed to load coding queue.'}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Invoice Coding Queue</h1>
        <StatusBadge label={`${invoices?.length ?? 0} pending`} variant="yellow" />
      </div>

      {successMessage && (
        <div style={{ padding: '0.75rem 1rem', backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.875rem', color: '#166534' }}>
          {successMessage}
        </div>
      )}

      {!invoices || invoices.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem' }}>
          No invoices pending review in your department.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {invoices.map((invoice) => (
            <div
              key={invoice.invoiceId}
              style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}
            >
              {/* Invoice summary row */}
              <div
                onClick={() => handleExpand(invoice.invoiceId)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr 1fr auto',
                  gap: '1rem',
                  padding: '0.75rem 1rem',
                  cursor: 'pointer',
                  backgroundColor: expandedInvoiceId === invoice.invoiceId ? '#f8fafc' : '#fff',
                  alignItems: 'center',
                  fontSize: '0.875rem',
                }}
                role="button"
                tabIndex={0}
                aria-expanded={expandedInvoiceId === invoice.invoiceId}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleExpand(invoice.invoiceId); }}
              >
                <div>
                  <div style={{ fontWeight: 500 }}>{invoice.vendorName ?? 'Unknown Vendor'}</div>
                  <div style={{ color: '#64748b', fontSize: '0.8rem' }}>#{invoice.invoiceNumber ?? '—'}</div>
                </div>
                <div>
                  <span style={{ color: '#64748b' }}>Amount: </span>
                  {invoice.totalAmount != null ? `$${Number(invoice.totalAmount).toLocaleString()}` : '—'}
                </div>
                <div>
                  <span style={{ color: '#64748b' }}>Date: </span>
                  {invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString() : '—'}
                </div>
                <div>
                  <StatusBadge label={invoice.departmentScope} variant="blue" />
                  {invoice.isDuplicate && (
                    <StatusBadge label="Duplicate" variant="red" />
                  )}
                </div>
                <div style={{ color: '#64748b' }}>
                  {expandedInvoiceId === invoice.invoiceId ? '▲' : '▼'}
                </div>
              </div>

              {/* Expanded line items coding section */}
              {expandedInvoiceId === invoice.invoiceId && (
                <div style={{ borderTop: '1px solid #e2e8f0', padding: '1rem', backgroundColor: '#f8fafc' }}>
                  {detailLoading ? (
                    <LoadingSpinner message="Loading line items…" />
                  ) : invoiceDetail ? (
                    <>
                      <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', fontWeight: 600 }}>
                        Line Items ({invoiceDetail.lineItems.length})
                      </h3>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>Description</th>
                            <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: '#475569' }}>Amount</th>
                            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>GL Code</th>
                            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>Cost Center</th>
                            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>Category</th>
                            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>Site</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoiceDetail.lineItems.map((li) => {
                            const edit = lineItemEdits[li.lineItemId];
                            return (
                              <tr key={li.lineItemId} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                <td style={{ padding: '0.4rem 0.5rem' }}>
                                  {li.description ?? '—'}
                                  {li.aiSuggestedGLCode && (
                                    <div style={{ fontSize: '0.7rem', color: '#8b5cf6' }}>
                                      AI suggested: {li.aiSuggestedGLCode}
                                      {li.aiConfidence != null && ` (${Math.round(li.aiConfidence * 100)}%)`}
                                    </div>
                                  )}
                                </td>
                                <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>
                                  {li.amount != null ? `$${Number(li.amount).toLocaleString()}` : '—'}
                                </td>
                                <td style={{ padding: '0.4rem 0.5rem' }}>
                                  <input
                                    type="text"
                                    value={edit?.glCode ?? ''}
                                    onChange={(e) => handleFieldChange(li.lineItemId, 'glCode', e.target.value)}
                                    placeholder="XXX-XXXX"
                                    aria-label={`GL Code for line item ${li.lineItemId}`}
                                    style={{ width: '90px', padding: '0.2rem 0.4rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.8rem' }}
                                  />
                                </td>
                                <td style={{ padding: '0.4rem 0.5rem' }}>
                                  <input
                                    type="text"
                                    value={edit?.costCenter ?? ''}
                                    onChange={(e) => handleFieldChange(li.lineItemId, 'costCenter', e.target.value)}
                                    placeholder="Cost center"
                                    aria-label={`Cost center for line item ${li.lineItemId}`}
                                    style={{ width: '90px', padding: '0.2rem 0.4rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.8rem' }}
                                  />
                                </td>
                                <td style={{ padding: '0.4rem 0.5rem' }}>
                                  <input
                                    type="text"
                                    value={edit?.budgetCategory ?? ''}
                                    onChange={(e) => handleFieldChange(li.lineItemId, 'budgetCategory', e.target.value)}
                                    placeholder="Category"
                                    aria-label={`Budget category for line item ${li.lineItemId}`}
                                    style={{ width: '90px', padding: '0.2rem 0.4rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.8rem' }}
                                  />
                                </td>
                                <td style={{ padding: '0.4rem 0.5rem' }}>
                                  <input
                                    type="text"
                                    value={edit?.siteNumber ?? ''}
                                    onChange={(e) => handleFieldChange(li.lineItemId, 'siteNumber', e.target.value)}
                                    placeholder="###"
                                    aria-label={`Site number for line item ${li.lineItemId}`}
                                    style={{ width: '60px', padding: '0.2rem 0.4rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.8rem' }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      {/* Action buttons */}
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
                        <Link
                          to={`/invoices/${invoice.invoiceId}`}
                          style={{ padding: '0.4rem 0.75rem', borderRadius: '4px', border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#334155', textDecoration: 'none', fontSize: '0.8rem' }}
                        >
                          View Detail
                        </Link>
                        <button
                          onClick={() => handleSaveCoding(invoice.invoiceId)}
                          disabled={codeMutation.isPending}
                          style={{
                            padding: '0.4rem 0.75rem',
                            borderRadius: '4px',
                            border: '1px solid #d1d5db',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                          }}
                        >
                          {codeMutation.isPending ? 'Saving…' : 'Save Coding'}
                        </button>
                        <button
                          onClick={() => handleSubmitForReview(invoice.invoiceId)}
                          disabled={submitMutation.isPending}
                          style={{
                            padding: '0.4rem 0.75rem',
                            borderRadius: '4px',
                            border: 'none',
                            backgroundColor: '#c8102e',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                          }}
                        >
                          {submitMutation.isPending ? 'Submitting…' : 'Submit for VP Review'}
                        </button>
                      </div>

                      {codeMutation.error && (
                        <p style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                          {(codeMutation.error as Error).message}
                        </p>
                      )}
                      {submitMutation.error && (
                        <p style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                          {(submitMutation.error as Error).message}
                        </p>
                      )}
                    </>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
